import {
  IconTicket, IconClockHour4, IconBolt, IconBook2, IconWorld, IconUsersGroup, IconChartBar,
  IconSparkles, IconRobot, IconMessageChatbot, IconWand, IconApi, IconWebhook, IconAppWindow,
  IconHeadset, IconCode, IconBriefcase, IconCoin, IconRocket, IconBuilding, IconBuildings,
  IconShoppingCart, IconPalette, IconBuildingBank, IconSchool, IconHeartRateMonitor, IconUsers,
  IconHistory, IconRoute, IconActivityHeartbeat, IconScale, IconCalculator, IconShieldLock,
  IconMail, IconFileText, IconLock, IconServer2, IconTrendingUp, IconCurrencyDollar,
} from '@tabler/icons-react';

export type NavIcon = typeof IconTicket;
export type NavItem = { label: string; desc: string; to: string; icon: NavIcon };
export type NavGroup = { title: string; items: NavItem[] };
export type NavFeature = { eyebrow: string; title: string; body: string; to: string; cta: string };
export type NavSection = { id: string; label: string; groups: NavGroup[]; feature?: NavFeature };

/**
 * Single source of truth for the marketing mega menu, the mobile drawer and the footer.
 * Every `to` must be a real page with its own copy (checked by scripts/check-nav-links.mjs).
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'platform',
    label: 'Platform',
    groups: [
      {
        title: 'Product',
        items: [
          { label: 'Ticketing', desc: 'Tickets and issues in one queue', to: '/ticketing', icon: IconTicket },
          { label: 'SLA tracking', desc: 'Policies, clocks and breach alerts', to: '/sla-tracking-software', icon: IconClockHour4 },
          { label: 'Automation', desc: 'If-this-then-that rules, free', to: '/helpdesk-automation', icon: IconBolt },
          { label: 'Helpdesk', desc: 'Branded help center and articles', to: '/knowledge-base-software', icon: IconBook2 },
          { label: 'Customer portal', desc: 'Customers follow their own requests', to: '/customer-portal-software', icon: IconWorld },
          { label: 'Team and roles', desc: 'Unlimited members, six roles', to: '/team-roles', icon: IconUsersGroup },
          { label: 'Analytics', desc: 'Volume, resolution and workload', to: '/helpdesk-analytics', icon: IconChartBar },
        ],
      },
      {
        title: 'Kora AI',
        items: [
          { label: 'AI reply drafting', desc: 'Suggested replies with sources', to: '/ai-reply-drafting-software', icon: IconSparkles },
          { label: 'AI resolution agent', desc: 'Answers tickets, asks before it sends', to: '/ai-customer-support-agent', icon: IconRobot },
          { label: 'AI Analyst', desc: 'Ask your ticket data in plain English', to: '/ai-analyst', icon: IconMessageChatbot },
          { label: 'AI ticket structuring', desc: 'Paste a message, get a clean ticket', to: '/ai-ticket-structuring', icon: IconWand },
          { label: 'Magic Helpdesk', desc: 'Drafts help articles from your site', to: '/magic-helpdesk', icon: IconBook2 },
        ],
      },
      {
        title: 'Developers',
        items: [
          { label: 'REST API', desc: '20+ endpoints, one key', to: '/developers/api', icon: IconApi },
          { label: 'Webhooks', desc: 'Signed events with retries', to: '/developers/webhooks', icon: IconWebhook },
          { label: 'Embedded sessions', desc: 'Kora inside your own product', to: '/developers/embedded-sessions', icon: IconAppWindow },
          { label: 'API reference', desc: 'Endpoints, auth and limits', to: '/docs', icon: IconCode },
        ],
      },
    ],
    feature: {
      eyebrow: 'Pricing',
      title: 'Pay only for outcomes',
      body: '$0.10 per drafted reply, $0.30 per confirmed resolution. No seats.',
      to: '/ai-helpdesk-pay-per-resolution',
      cta: 'See how it works',
    },
  },
  {
    id: 'solutions',
    label: 'Solutions',
    groups: [
      {
        title: 'Teams',
        items: [
          { label: 'Customer support', desc: 'Answer customers faster', to: '/solutions/customer-support', icon: IconHeadset },
          { label: 'IT and internal ops', desc: 'Employee requests, one queue', to: '/solutions/it-teams', icon: IconServer2 },
          { label: 'Product and engineering', desc: 'Bugs and issues, tracked', to: '/solutions/product-engineering', icon: IconCode },
          { label: 'Finance', desc: 'Invoices, approvals, requests', to: '/solutions/finance-teams', icon: IconCurrencyDollar },
          { label: 'Business and growth', desc: 'Sales and marketing requests', to: '/solutions/business-growth', icon: IconTrendingUp },
          { label: 'Every team', desc: 'The whole company in one workspace', to: '/solutions/company-wide', icon: IconUsers },
        ],
      },
    ],
    // Industries and Business size return when those pages have real substance (founder, 2026-10-29).
    feature: {
      eyebrow: 'Collaboration',
      title: 'Kora for every team',
      body: 'One workspace, unlimited members. Every team raises and tracks requests.',
      to: '/solutions/company-wide',
      cta: 'See how teams use Kora',
    },
  },
  {
    id: 'resources',
    label: 'Resources',
    groups: [
      {
        title: 'Product updates',
        items: [
          { label: 'Changelog', desc: 'What shipped and when', to: '/changelog', icon: IconHistory },
          { label: 'Roadmap', desc: 'What we build next', to: '/roadmap', icon: IconRoute },
          { label: 'Status', desc: 'Live system status', to: '/status', icon: IconActivityHeartbeat },
        ],
      },
      {
        title: 'Learn',
        items: [
          { label: 'API docs', desc: 'Reference for every endpoint', to: '/docs', icon: IconCode },
          { label: 'Webhooks', desc: 'Events, signing and retries', to: '/developers/webhooks', icon: IconWebhook },
          { label: 'Compare', desc: 'Kora against nine other tools', to: '/compare', icon: IconScale },
          { label: 'Cost calculator', desc: 'Per-seat versus per-ticket', to: '/helpdesk-cost-calculator', icon: IconCalculator },
        ],
      },
      {
        title: 'Company',
        items: [
          { label: 'About', desc: 'Who builds Kora and why', to: '/about', icon: IconUsers },
          { label: 'Contact', desc: 'Talk to the founder', to: '/contact', icon: IconMail },
          { label: 'Security', desc: 'How your data is protected', to: '/security', icon: IconShieldLock },
          { label: 'Privacy', desc: 'How we handle personal data', to: '/privacy', icon: IconLock },
          { label: 'Terms', desc: 'The agreement', to: '/terms', icon: IconFileText },
        ],
      },
    ],
  },
];

export const NAV_PRICING = { label: 'Pricing', to: '/pricing', hint: '$0.10 per ticket' };

/** Flat list of every menu destination, used by the link checker and the sitemap. */
export const NAV_LINKS_FLAT: string[] = Array.from(new Set(
  NAV_SECTIONS.flatMap((s) => [...s.groups.flatMap((g) => g.items.map((i) => i.to)), ...(s.feature ? [s.feature.to] : [])]),
));
